// src/pages/onboarding-screens/vehicle_owner_form.js
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Pressable,
  ScrollView,
  Platform,
  Image,
} from "react-native";
import * as DocumentPicker from "expo-document-picker";

import ConsentModal from "../../components/ConsentModal";
import { AppContext } from "../../../App";
import { useFormNavigation } from "../../utils/navigationUtils";
import Header from "../../components/Header";
import { useTranslation } from "../../i18n";

const COLORS = {
  primary: "#024A59",
  teal: "#1CA7AE",
  grey: "#F6FAFB",
  text: "#0b1a1f",
  muted: "#6B7280",
  error: "#DC2626",
};

// ----------------- helpers -----------------
const normalizeSpaces = (s) => String(s || "").replace(/\s+/g, " ").trim();

/**
 * Valid person name rules:
 * - letters only (supports accents) + spaces + apostrophe + hyphen
 * - must contain at least 1 letter total
 */
const isValidPersonName = (raw) => {
  const v = normalizeSpaces(raw);
  if (!v) return false;

  // Count letters (unicode letters)
  const letters = (v.match(/\p{L}/gu) || []).length;
  if (letters < 1) return false;

  // Allow letters + spaces + apostrophes + hyphens
  const ok = /^[\p{L}]+(?:[ '\-][\p{L}]+)*$/u.test(v);
  return ok;
};

// CheckRow component for the checkbox
function CheckRow({ label, checked, onPress }) {
  return (
    <Pressable onPress={onPress} style={styles.checkRow}>
      <View style={[styles.checkBox, checked && styles.checkBoxOn]}>
        {checked && <Text style={styles.checkMark}>✓</Text>}
      </View>
      <Text style={styles.checkLabel}>{label}</Text>
    </Pressable>
  );
}

export default function VehicleOwnerFormScreen({ navigation }) {
  const { verification, setVerification, setFormProgress } = useContext(AppContext);
  const { updateCurrentStep } = useFormNavigation();
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState(verification?.owner?.firstName || "");
  const [lastName, setLastName] = useState(verification?.owner?.lastName || "");
  const [idCardFile, setIdCardFile] = useState(verification?.pendingUploads?.ownerId || null);
  const [showConsentModal, setShowConsentModal] = useState(false);

  const [skipDocument, setSkipDocument] = useState(
    verification?.owner?.skipIdCard === true
  );

  // ✅ Field-level errors (show only when non-empty + invalid)
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);

useEffect(() => {
  setFormProgress((prev) => {
    if (prev?.currentStep === "vehicle_owner_form_screen") return prev;
    return { ...prev, currentStep: "vehicle_owner_form_screen" };
  });
}, [setFormProgress]);


  // ✅ Live validation (NO error when empty)
  useEffect(() => {
    const fn = normalizeSpaces(firstName);
    const ln = normalizeSpaces(lastName);

    if (fn.length === 0) setFirstNameError(null);
    else setFirstNameError(isValidPersonName(fn) ? null : t("errors.invalidName"));

    if (ln.length === 0) setLastNameError(null);
    else setLastNameError(isValidPersonName(ln) ? null : t("errors.invalidName"));
  }, [firstName, lastName, t]);

  // Auto-save in context
// src/pages/onboarding-screens/vehicle_owner_form.js - Updated auto-save

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setVerification((prev) => ({
        ...prev,
        owner: {
          ...prev.owner,
          type: "PHYSIQUE",
          firstName: normalizeSpaces(firstName),
          lastName: normalizeSpaces(lastName),
          fullName: `${normalizeSpaces(firstName)} ${normalizeSpaces(lastName)}`.trim(),
          skipIdCard: skipDocument,
        },
        pendingUploads: {
          ...(prev.pendingUploads || {}),
          ownerId: skipDocument ? null : idCardFile || null,
        },
      }));
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [firstName, lastName, idCardFile, skipDocument, setVerification]);

  // ✅ Continue enabled only when names are valid + doc ok
  const allFieldsFilled = useMemo(() => {
    const fnOk = isValidPersonName(firstName);
    const lnOk = isValidPersonName(lastName);
    const docOk = skipDocument || !!idCardFile;
    return fnOk && lnOk && docOk;
  }, [firstName, lastName, idCardFile, skipDocument]);

  const pickIdCard = async () => {
    if (skipDocument) return;

    if (Platform.OS === "web") {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".pdf,image/*";
      input.onchange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        setIdCardFile({
          uri: null,
          name: file.name,
          mimeType: file.type || "application/octet-stream",
          type: file.type || "application/octet-stream",
          __webFile: file,
        });
      };
      input.click();
      return;
    }

    const result = await DocumentPicker.getDocumentAsync({
      type: ["application/pdf", "image/*"],
      multiple: false,
    });

    if (result.type === "cancel") return;

    const asset = result.assets?.[0];
    if (!asset) return;

    setIdCardFile({
      uri: asset.uri,
      name: asset.name || "piece_identite",
      mimeType: asset.mimeType || "application/octet-stream",
      type: asset.mimeType || "application/octet-stream",
    });
  };

  const handleContinue = () => {
    if (!allFieldsFilled) return;

    setVerification((prev) => ({
      ...prev,
      owner: {
        type: "PHYSIQUE",
        firstName: normalizeSpaces(firstName),
        lastName: normalizeSpaces(lastName),
        skipIdCard: skipDocument,
      },
      pendingUploads: {
        ...(prev.pendingUploads || {}),
        ownerId: skipDocument ? null : idCardFile,
      },
    }));

    setFormProgress((prev) => ({
      ...prev,
      completedSteps: [
        ...new Set([...(prev.completedSteps || []), "vehicle_owner_form_screen"]),
      ],
    }));

    setShowConsentModal(true);
  };

  return (
    <View style={styles.screen}>
      <Header />

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <Pressable
          style={styles.backButton}
          onPress={() => navigation.navigate("ownership_screen")}
        >
          <Text style={styles.backButtonIcon}>←</Text>
        </Pressable>

        <Text style={styles.title}>{t("ownerForm.title")}</Text>
        <Text style={styles.subtitle}>{t("ownerForm.subtitle")}</Text>

        <View style={styles.card}>
          <Text style={styles.label}>{t("ownerForm.firstName")}</Text>
          <TextInput
            value={firstName}
            onChangeText={(v) => setFirstName(v)} // ✅ allow typing anything
            placeholder={t("ownerForm.firstNamePlaceholder")}
            placeholderTextColor="#9aa3af"
            style={styles.input} // ✅ no red border change requirement can be applied by removing inputError if you want
            autoCorrect={false}
          />
          {firstNameError ? <Text style={styles.errorText}>{firstNameError}</Text> : null}

          <Text style={[styles.label, { marginTop: 12 }]}>{t("ownerForm.lastName")}</Text>
          <TextInput
            value={lastName}
            onChangeText={(v) => setLastName(v)} // ✅ allow typing anything
            placeholder={t("ownerForm.lastNamePlaceholder")}
            placeholderTextColor="#9aa3af"
            style={styles.input}
            autoCorrect={false}
          />
          {lastNameError ? <Text style={styles.errorText}>{lastNameError}</Text> : null}

          <View style={{ height: 16 }} />

          <CheckRow
            label={t("documentUpload.skip")}
            checked={skipDocument}
            onPress={() => {
              setSkipDocument((v) => !v);
              if (!skipDocument) setIdCardFile(null);
            }}
          />

          {!skipDocument && (
            <>
              <Pressable style={styles.uploadCard} onPress={pickIdCard}>
                <View style={styles.uploadInner}>
                  <Image
                    source={require("../../../assets/file_upload.png")}
                    style={styles.uploadIcon}
                  />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.uploadTitle}>{t("ownerForm.addIdCard")}</Text>
                    <Text style={styles.uploadHint}>{t("ownerForm.fileFormats")}</Text>
                  </View>
                </View>
              </Pressable>

              {!!idCardFile?.name && (
                <View style={styles.fileRow}>
                  <Text style={styles.fileName}>{idCardFile.name}</Text>
                  <Pressable onPress={() => setIdCardFile(null)}>
                    <Text style={styles.removeText}>{t("ownerForm.remove")}</Text>
                  </Pressable>
                </View>
              )}
            </>
          )}
        </View>

        <Pressable
          style={[styles.nextButton, !allFieldsFilled && styles.nextButtonDisabled]}
          disabled={!allFieldsFilled}
          onPress={handleContinue}
        >
          <Text style={styles.nextButtonText}>{t("common.continue")}</Text>
        </Pressable>
      </ScrollView>

      {showConsentModal && (
        <ConsentModal onDismiss={() => setShowConsentModal(false)} navigation={navigation} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: COLORS.grey,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  backButtonIcon: {
    fontSize: 18,
    color: COLORS.text,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    color: COLORS.text,
    marginTop: 4,
  },
  subtitle: {
    marginTop: 8,
    color: COLORS.muted,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  label: {
    fontSize: 13,
    color: COLORS.muted,
    marginBottom: 6,
  },
  input: {
    height: 48,
    borderRadius: 12,
    backgroundColor: "#f9fafb",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    paddingHorizontal: 12,
    color: COLORS.text,
  },
  errorText: {
    marginTop: 6,
    color: COLORS.error,
    fontSize: 12,
    fontWeight: "600",
  },
  checkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
  },
  checkBox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    alignItems: "center",
    justifyContent: "center",
  },
  checkBoxOn: {
    backgroundColor: COLORS.teal,
    borderColor: COLORS.teal,
  },
  checkMark: {
    color: "#fff",
    fontWeight: "900",
  },
  checkLabel: {
    fontWeight: "600",
    color: COLORS.text,
  },
  uploadCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#f9fafb",
    padding: 12,
  },
  uploadInner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  uploadIcon: {
    width: 26,
    height: 26,
  },
  uploadTitle: {
    fontSize: 14,
    fontWeight: "600",
    color: COLORS.text,
  },
  uploadHint: {
    fontSize: 12,
    color: COLORS.muted,
    marginTop: 2,
  },
  fileRow: {
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  fileName: {
    flex: 1,
    color: COLORS.text,
    marginRight: 10,
  },
  removeText: {
    color: "#ef4444",
    fontWeight: "600",
  },
  nextButton: {
    marginTop: 16,
    backgroundColor: COLORS.teal,
    borderRadius: 14,
    paddingVertical: 14,
    alignItems: "center",
  },
  nextButtonDisabled: {
    opacity: 0.5,
  },
  nextButtonText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 16,
  },
});
